import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import {
  GetSegmentContactsRequestPayload,
  GetSegmentContactsResponse,
  GetSegmentsResponse,
  ISegment,
  ISegmentForm,
  SegmentFilters,
  UpdateSegmentForm,
} from 'types/segments/segments.types';
import { UploadSegmentContactsRequestPayload } from 'store/segments/segments.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const getSegments = async (
  filters: SegmentFilters
): Promise<GetSegmentsResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters.limit !== undefined)
      params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.type) params.append('type', filters.type);
    const response = await api.get(`/segments?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching segments:', error);
    throw error;
  }
};

export const getSegmentById = async (id: string): Promise<ISegment> => {
  try {
    const response = await api.get(`/segments/${id}`);
    return response.data.segment;
  } catch (error) {
    console.error('Error fetching segment:', error);
    throw error;
  }
};

export const createSegment = async (
  segment: ISegmentForm
): Promise<ISegment> => {
  try {
    const response = await api.post('/segments', segment);
    return response.data.segment;
  } catch (error) {
    console.error('Error upserting segment:', error);
    throw error;
  }
};

export const updateSegment = async (
  segment: UpdateSegmentForm
): Promise<ISegment> => {
  try {
    const response = await api.patch(`/segments/${segment.id}`, segment.data);
    return response.data.segment;
  } catch (error) {
    console.error('Error updating segment:', error);
    throw error;
  }
};

export const deleteSegment = async (id: string): Promise<void> => {
  try {
    await api.delete(`/segments/${id}`);
  } catch (error) {
    console.error('Error deleting segment:', error);
    throw error;
  }
};

export const uploadSegmentContacts = async (
  data: UploadSegmentContactsRequestPayload
): Promise<void> => {
  const { id, contacts, lookupBy } = data;
  const payload = {
    contacts,
    lookupBy,
  };
  try {
    await api.post(`/segments/${id}/contacts`, payload);
  } catch (error) {
    console.error('Error uploading segment contacts:', error);
    throw error;
  }
};

export const getSegmentContacts = async (
  payload: GetSegmentContactsRequestPayload
): Promise<GetSegmentContactsResponse> => {
  try {
    const { id, ...rest } = payload;
    const response = await api.post(`/segments/contacts/${id}`, rest);
    return response.data;
  } catch (error) {
    console.error('Error fetching segment contacts:', error);
    throw error;
  }
};

export const exportSegmentContacts = async (
  segmentId: string
): Promise<Blob> => {
  try {
    const response = await api.get(`/segments/contacts/export/${segmentId}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting segment contacts:', error);
    throw error;
  }
};

export const removeContactFromSegment = async (
  segmentId: string,
  contactId: string
): Promise<void> => {
  try {
    const response = await api.delete(
      `/segments/${segmentId}/contacts/${contactId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error removing contact from segment:', error);
    throw error;
  }
};

export interface AddContactsToSegmentPayload {
  contactIds: string[];
}

export const addContactsToSegment = async (
  segmentId: string,
  payload: AddContactsToSegmentPayload
): Promise<void> => {
  try {
    await api.post(`/segments/${segmentId}/contacts/add`, payload);
  } catch (error) {
    console.error('Error adding contacts to segment:', error);
    throw error;
  }
};
